// E37-t6 — A MINIMAL MARKDOWN READER for the file browser, and nothing more.
//
// WHY THIS EXISTS AT ALL (the one-renderer question, answered honestly): the
// engine's markdown pipeline (docujoint dist/render/markdown-html.ts mdToHtml)
// is definition-bound — it takes a CompiledDefinition, a ref-state map, a
// media resolver and an embed resolver, because it renders a CONCEPT inside a
// baked dashboard. This page renders a FILE: the vault as checked in, no
// format resolved, no definition in hand. The design named this exact seam
// and its fallback — "`.md` via the `renderMarkdownBody` engine export …
// vendored web/src/lib/markdown.ts only if extraction fails" (e35-37-design.md
// §5 E37-t6 / §3.2 render layer) — and the pinned engine (0.20.0) ships no
// renderMarkdownBody, so this is the fallback, taken by name. When the export
// lands, this file is what it deletes.
//
// SCOPE, deliberately small: headings, paragraphs, fenced code, lists, tables,
// blockquotes, rules, inline code/bold/italic/links. Frontmatter is shown as
// the data it is (a muted block), not hidden. Images become a muted note —
// this surface has no asset door, and a broken <img> would claim one exists.
// Anything not recognised renders as the paragraph text it literally is:
// showing an author's bytes is this page's promise, prettiness is not.
//
// SAFETY: every character of input is HTML-escaped BEFORE any tag this file
// emits is added, so no author byte can open an element or an attribute. Link
// hrefs are emitted only for http(s) targets; relative `.md` targets become
// `data-doc` attributes for the page to resolve inside the tree — never a raw
// href the SPA's router would misread.

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

/** Inline markdown over ALREADY-ESCAPED text. Order matters: code spans first
 *  (their content admits no further markup), then images before links. */
function inline(text: string): string {
  let s = text;
  const codes: string[] = [];
  // code spans are lifted out first so `**not bold**` stays literal inside them
  s = s.replace(/`([^`]+)`/g, (_m, body: string) => {
    codes.push(`<code>${body}</code>`);
    return `\u0000${codes.length - 1}\u0000`;
  });
  // images: a muted note — no asset door exists on this surface (header note)
  s = s.replace(/!\[([^\]]*)\]\(([^)\s]*)\)/g, (_m, alt: string, url: string) => {
    return `<span class="djmd-note">[image: ${alt || url}]</span>`;
  });
  // links: http(s) get a real href; relative .md targets become data-doc for
  // the page's own tree navigation; anything else stays visible text + target
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, url: string) => {
    if (/^https?:\/\//.test(url)) {
      return `<a href="${url}" target="_blank" rel="noreferrer">${label}</a>`;
    }
    if (/\.md(#[^)]*)?$/i.test(url) && !url.startsWith('#')) {
      return `<a data-doc="${url.replace(/#.*$/, '')}" href="#">${label}</a>`;
    }
    return `<span class="djmd-note">${label} (${url})</span>`;
  });
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[\s(])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>');
  s = s.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => codes[Number(i)] ?? '');
  return s;
}

/** Split a table row on pipes; leading/trailing pipe optional. */
const rowCells = (line: string): string[] => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((c) => c.trim());
};

const isSeparatorRow = (line: string): boolean =>
  /^\s*\|?\s*:?-{2,}/.test(line) && rowCells(line).every((c) => /^:?-+:?$/.test(c));

export interface RenderedDoc {
  /** the document body, as HTML this module produced from escaped input */
  html: string;
  /** the frontmatter's raw lines (between the `---` fences), or [] */
  frontmatter: string[];
}

/** The whole document: frontmatter split off as data, the body rendered. */
export function renderMarkdownDoc(src: string): RenderedDoc {
  let body = src;
  const frontmatter: string[] = [];
  const fm = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(src);
  if (fm) {
    frontmatter.push(...fm[1].split(/\r?\n/));
    body = src.slice(fm[0].length);
  }
  return { html: renderMarkdown(body), frontmatter };
}

/** Markdown → HTML, line-oriented, escape-first. */
export function renderMarkdown(md: string): string {
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let i = 0;

  const paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length === 0) return;
    out.push(`<p>${inline(esc(paragraph.join(' ')))}</p>`);
    paragraph.length = 0;
  };

  while (i < lines.length) {
    const line = lines[i];

    // fenced code — verbatim until the closing fence (or the end of file:
    // an unclosed fence still renders as the code it visibly is)
    const fence = /^```(.*)$/.exec(line);
    if (fence) {
      flush();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        code.push(lines[i]);
        i++;
      }
      i++; // the closing fence, when there was one
      const lang = esc(fence[1].trim());
      out.push(
        `<pre class="djmd-code"${lang ? ` data-lang="${lang}"` : ''}><code>${esc(code.join('\n'))}</code></pre>`,
      );
      continue;
    }

    if (/^\s*$/.test(line)) {
      flush();
      i++;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      const level = heading[1].length;
      out.push(`<h${level}>${inline(esc(heading[2].trim()))}</h${level}>`);
      i++;
      continue;
    }

    if (/^(\s*)(---+|\*\*\*+|___+)\s*$/.test(line)) {
      flush();
      out.push('<hr>');
      i++;
      continue;
    }

    if (/^>\s?/.test(line)) {
      flush();
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      out.push(`<blockquote>${renderMarkdown(quote.join('\n'))}</blockquote>`);
      continue;
    }

    // a table needs its separator row to BE a table — otherwise the pipes are
    // just a paragraph that contains pipes, and it renders as what it is
    if (/^\s*\|/.test(line) && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      flush();
      const header = rowCells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        rows.push(rowCells(lines[i]));
        i++;
      }
      const th = header.map((c) => `<th>${inline(esc(c))}</th>`).join('');
      const trs = rows
        .map((cells) => `<tr>${cells.map((c) => `<td>${inline(esc(c))}</td>`).join('')}</tr>`)
        .join('');
      out.push(
        `<div class="djmd-tablewrap"><table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></div>`,
      );
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/;
    if (bullet.test(line)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && bullet.test(lines[i])) {
        items.push(`<li>${inline(esc((bullet.exec(lines[i]) as RegExpExecArray)[1]))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    const ordered = /^\s*\d+[.)]\s+(.*)$/;
    if (ordered.test(line)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && ordered.test(lines[i])) {
        items.push(`<li>${inline(esc((ordered.exec(lines[i]) as RegExpExecArray)[1]))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    paragraph.push(line.trim());
    i++;
  }
  flush();
  return out.join('\n');
}
